import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FavoriteBorder,
  AccountBalance,
  Settings as AdminIcon,
  Logout,
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  PersonOutline,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import BrandLogo from './BrandLogo';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    api.get('/daily-matches/notifications')
      .then((response) => setUnreadCount(response.data?.unread_count || 0))
      .catch(() => undefined);
  }, [user?.id]);

  const isAdmin = user?.role && ['SUPER_ADMIN', 'CONTENT_MODERATOR', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'ID_VERIFICATION_ADMIN'].includes(user.role);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleMenuClose();
    setMobileDrawerOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const mobileDrawer = (
    <Box sx={{ width: 280 }} role="presentation" onClick={() => setMobileDrawerOpen(false)}>
      <Box sx={{ p: 2, backgroundColor: '#7b0011' }}>
        <BrandLogo to="/my-profile" logoHeight={30} showText={false} />
      </Box>
      <List>
        <ListItem button onClick={() => handleNavigate('/my-profile')}>
          <ListItemIcon><DashboardIcon /></ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/my-profile?openCompleteProfile=1')}>
          <ListItemIcon><PersonOutline /></ListItemIcon>
          <ListItemText primary="Update Profile" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/my-profile?openMatchPreferences=1')}>
          <ListItemIcon><AdminIcon /></ListItemIcon>
          <ListItemText primary="Match Preferences" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/')}>
          <ListItemIcon><HomeIcon /></ListItemIcon>
          <ListItemText primary="Search Profiles" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/my-connections')}>
          <ListItemIcon><FavoriteBorder /></ListItemIcon>
          <ListItemText primary="Unlocked Profiles" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/wallet')}>
          <ListItemIcon><AccountBalance /></ListItemIcon>
          <ListItemText primary="Recharge Account" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/activation-status')}>
          <ListItemIcon><VerifiedUser /></ListItemIcon>
          <ListItemText primary="Activation Status" />
        </ListItem>
        <ListItem button onClick={() => handleNavigate('/change-password')}>
          <ListItemIcon><AdminIcon /></ListItemIcon>
          <ListItemText primary="Change Password" />
        </ListItem>
      </List>

      {isAdmin && (
        <>
          <Divider />
          <List>
            <ListItem button onClick={() => handleNavigate('/admin')}>
              <ListItemIcon><AdminIcon /></ListItemIcon>
              <ListItemText primary="Admin Dashboard" />
            </ListItem>
            <ListItem button onClick={() => handleNavigate('/admin/recharge-management')}>
              <ListItemText primary="Recharge Management" />
            </ListItem>
            <ListItem button onClick={() => handleNavigate('/admin/profile-reviews')}>
              <ListItemText primary="Profile Reviews" />
            </ListItem>
            <ListItem button onClick={() => handleNavigate('/admin/users')}>
              <ListItemText primary="User Management" />
            </ListItem>
            <ListItem button onClick={() => handleNavigate('/admin/activation-status')}>
              <ListItemText primary="Activation Status" />
            </ListItem>
          </List>
        </>
      )}

      <Divider />
      <List>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon><Logout /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" sx={{ backgroundColor: '#7b0011' }}>
      <Toolbar sx={{ minHeight: 64 }}>
        {isMobile && (
          <IconButton color="inherit" onClick={() => setMobileDrawerOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ marginRight: 'auto', display: 'flex', alignItems: 'center' }}>
          <BrandLogo to="/" logoHeight={34} showText={false} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {!isMobile && (
            <>
              <Button
                color="inherit"
                onClick={() => navigate('/')}
                startIcon={<HomeIcon />}
                sx={{ textTransform: 'none' }}
              >
                Search Profiles
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/my-profile')}
                startIcon={<DashboardIcon />}
                sx={{ textTransform: 'none' }}
              >
                Dashboard
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/my-profile?openCompleteProfile=1')}
                startIcon={<PersonOutline />}
                sx={{ textTransform: 'none' }}
              >
                Update Profile
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/my-profile?openMatchPreferences=1')}
                startIcon={<AdminIcon />}
                sx={{ textTransform: 'none' }}
              >
                Match Preferences
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/my-connections')}
                startIcon={<FavoriteBorder />}
                sx={{ textTransform: 'none' }}
              >
                Unlocked Profiles
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/wallet')}
                startIcon={<AccountBalance />}
                sx={{ textTransform: 'none' }}
              >
                Recharge Account
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/activation-status')}
                startIcon={<VerifiedUser />}
                sx={{ textTransform: 'none' }}
              >
                Activation
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/change-password')}
                startIcon={<AdminIcon />}
                sx={{ textTransform: 'none' }}
              >
                Change Password
              </Button>

              {isAdmin && (
                <Button
                  color="inherit"
                  onClick={handleAdminMenuOpen}
                  startIcon={<AdminIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Admin
                </Button>
              )}
            </>
          )}

          <Button
            onClick={handleMenuOpen}
            sx={{
              padding: 0,
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Badge color="error" variant="dot" invisible={unreadCount === 0} overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <Avatar sx={{ width: 32, height: 32, backgroundColor: '#c89428' }}>
                {user?.first_name?.charAt(0).toUpperCase()}
              </Avatar>
            </Badge>
          </Button>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { navigate('/my-profile'); handleMenuClose(); }}>
              Dashboard
            </MenuItem>
            {user?.id && (
              <MenuItem onClick={() => { navigate('/my-live-profile'); handleMenuClose(); }}>
                My Live Profile
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ marginRight: 1 }} />
              Logout
            </MenuItem>
          </Menu>

          {/* Admin Menu */}
          {!isMobile && (
            <Menu
              anchorEl={adminAnchorEl}
              open={Boolean(adminAnchorEl)}
              onClose={handleAdminMenuClose}
            >
              <MenuItem onClick={() => { navigate('/admin'); handleAdminMenuClose(); }}>
                Dashboard
              </MenuItem>
              <MenuItem onClick={() => { navigate('/admin/recharge-management'); handleAdminMenuClose(); }}>
                Recharge Management
              </MenuItem>
              <MenuItem onClick={() => { navigate('/admin/profile-reviews'); handleAdminMenuClose(); }}>
                Profile Reviews
              </MenuItem>
              <MenuItem onClick={() => { navigate('/admin/users'); handleAdminMenuClose(); }}>
                User Management
              </MenuItem>
              <MenuItem onClick={() => { navigate('/admin/activation-status'); handleAdminMenuClose(); }}>
                Activation Status
              </MenuItem>
            </Menu>
          )}
        </Box>
      </Toolbar>

      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      >
        {mobileDrawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
